import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";

// ===== EAGER IMPORTS (critical path - always needed) =====
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Chatbot from "./components/Chatbot";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";

// Context providers (always needed)
import { DarkModeProvider } from "./context/DarkModeContext";
import { CartProvider } from "./context/CartContext";
import { ConversationProvider } from "./context/ConversationContext";
import { AuthProvider } from "./context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LoadScript } from "@react-google-maps/api";
import { Toaster } from "react-hot-toast";

const queryClient = new QueryClient();

// ===== LAZY IMPORTS (code-split per route) =====

// Public pages
const CompleteProfile = lazy(() => import("./pages/CompleteProfile.jsx"));
const Contact = lazy(() => import("./pages/Contact"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyOtp = lazy(() => import("./pages/VerifyOTP"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const About = lazy(() => import("./pages/About"));
const Materials = lazy(() => import("./pages/Materials"));
const ProductDetails = lazy(() => import("./pages/ProductDetails.jsx"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Profile = lazy(() => import("./pages/Profile"));
const HelpAndSupport = lazy(() => import("./components/HelpAndSupport"));

// Customer pages
const CustomerLayout = lazy(() => import("./layout/CustomerLayout"));
const CustomerDashboard = lazy(() => import("./pages/customer/CustomerDashboard"));
const CustomerSettingsPage = lazy(() => import("./pages/customer/CustomerSettingsPage.jsx"));
const CustomerNotifications = lazy(() => import("./pages/customer/CustomerNotifications"));
const OrderTracking = lazy(() => import("./pages/customer/OrderTracking"));
const MyOrders = lazy(() => import("./pages/customer/MyOrders"));
const Cart = lazy(() => import("./pages/customer/Cart"));
const Checkout = lazy(() => import("./pages/customer/Checkout"));
const OrderSuccess = lazy(() => import("./pages/customer/OrderSuccess"));
const OrderDetails = lazy(() => import("./pages/customer/OrderDetails"));
const WriteReview = lazy(() => import("./pages/customer/WriteReview"));
const Wishlist = lazy(() => import("./pages/customer/Wishlist"));
const AddressManagement = lazy(() => import("./pages/customer/AddressManagement"));
const QuickReorder = lazy(() => import("./pages/customer/QuickReorder"));
const Invoices = lazy(() => import("./pages/customer/Invoices"));
const PaymentMethods = lazy(() => import("./pages/customer/PaymentMethods"));
const Offers = lazy(() => import("./pages/customer/Offers"));
const NearbySuppliers = lazy(() => import("./pages/customer/NearbySuppliers"));
const ProductAlerts = lazy(() => import("./pages/customer/ProductAlerts"));
const SpendingAnalytics = lazy(() => import("./pages/customer/SpendingAnalytics"));
const RequestQuote = lazy(() => import("./pages/customer/RequestQuote"));
const MyQuotes = lazy(() => import("./pages/customer/MyQuotes"));
const QuoteDetails = lazy(() => import("./pages/customer/QuoteDetails"));

// Supplier pages
const SupplierDashboard = lazy(() => import("./pages/supplier/SupplierDashboard"));
const MyProducts = lazy(() => import("./pages/supplier/MyProducts"));
const AddProduct = lazy(() => import("./pages/supplier/AddProductEnhanced"));
const EditProduct = lazy(() => import("./pages/supplier/EditProduct"));
const ProductDetail = lazy(() => import("./pages/supplier/ProductDetail.jsx"));
const LocationPage = lazy(() => import("./pages/supplier/LocationPage.jsx"));
const BusinessProfilePage = lazy(() => import("./pages/supplier/BusinessProfilePage.jsx"));
const CategoriesPage = lazy(() => import("./pages/supplier/CategoriesPage.jsx"));
const OrdersPage = lazy(() => import("./pages/supplier/OrdersPage.jsx"));
const BulkUpload = lazy(() => import("./pages/supplier/BulkUpload.jsx"));
const PaymentsPage = lazy(() => import("./pages/supplier/PaymentsPage.jsx"));
const AnalyticsPage = lazy(() => import("./pages/supplier/AnalyticsPage.jsx"));
const SyncInventoryPage = lazy(() => import("./pages/supplier/SyncInventoryPage.jsx"));
const ManageOffersPage = lazy(() => import("./pages/supplier/ManageOffersPage.jsx"));
const OfferFormPage = lazy(() => import("./pages/supplier/OfferFormPage.jsx"));
const ActivityLogsPage = lazy(() => import("./pages/supplier/ActivityLogsPage"));
const CustomerFeedbackPage = lazy(() => import("./pages/supplier/CustomerFeedbackPage"));
const DeliveryStatusPage = lazy(() => import("./pages/supplier/DeliveryStatusPage"));
const NotificationsPage = lazy(() => import("./pages/supplier/NotificationsPage"));
const TopProductsPage = lazy(() => import("./pages/supplier/TopProductsPage"));
const QuoteRequests = lazy(() => import("./pages/supplier/QuoteRequests"));
const RespondToQuote = lazy(() => import("./pages/supplier/RespondToQuote"));
// Admin pages
const AdminLayout = lazy(() => import("./layout/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const ProductModeration = lazy(() => import("./pages/admin/ProductModeration"));
const ComplaintManagement = lazy(() => import("./pages/admin/ComplaintManagement"));
const ContactMessages = lazy(() => import("./pages/admin/ContactMessages"));
const Announcements = lazy(() => import("./pages/admin/Announcements"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

// Legal pages
const CorePolicies = lazy(() => import("./pages/legal/CorePolicies"));
const SmartPlatform = lazy(() => import("./pages/legal/SmartPlatform"));
const SupplierPartner = lazy(() => import("./pages/legal/SupplierPartner"));
const CustomerPlatform = lazy(() => import("./pages/legal/CustomerPlatform"));
const Monetization = lazy(() => import("./pages/legal/Monetization"));
const TermsAndConditions = lazy(() => import("./pages/legal/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy.jsx"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer.jsx"));
const DataProtection = lazy(() => import("./pages/legal/DataProtection.jsx"));
const SecurityPractices = lazy(() => import("./pages/legal/SecurityPractices.jsx"));
const SupplierAgreement = lazy(() => import("./pages/legal/SupplierAgreement.jsx"));
const ProductGuidelines = lazy(() => import("./pages/legal/ProductGuidelines.jsx"));
const LogisticsAgreement = lazy(() => import("./pages/legal/LogisticsAgreement.jsx"));
const PricingPolicy = lazy(() => import("./pages/legal/PricingPolicy.jsx"));
const ReturnRefundPolicy = lazy(() => import("./pages/legal/ReturnRefundPolicy.jsx"));
const ShippingPolicy = lazy(() => import("./pages/legal/ShippingPolicy.jsx"));
const CancellationPolicy = lazy(() => import("./pages/legal/CancellationPolicy.jsx"));
const OrderPlacementTracking = lazy(() => import("./pages/legal/OrderPlacementTracking"));
const PaymentRefundGuidelines = lazy(() => import("./pages/legal/PaymentRefundGuidelines"));
const ReviewSystemIntegrity = lazy(() => import("./pages/legal/ReviewSystemIntegrity"));
const SupportGrievanceRedressal = lazy(() => import("./pages/legal/SupportGrievanceRedressal"));
const CustomerAgreement = lazy(() => import("./pages/legal/CustomerAgreement"));
const PlatformPolicy = lazy(() => import("./pages/legal/PlatformPolicy"));
const GeneralReviewPolicy = lazy(() => import("./pages/legal/GeneralReviewPolicy"));
const Disputes = lazy(() => import("./pages/legal/Disputes"));
const LocationTerms = lazy(() => import("./pages/legal/LocationTerms"));
const LaborTerms = lazy(() => import("./pages/legal/LaborTerms"));
const AIGeneratedInsightsDisclaimer = lazy(() => import("./pages/legal/AIGeneratedInsightsDisclaimer"));
const AlgorithmicFairnessPolicy = lazy(() => import("./pages/legal/AlgorithmicFairnessPolicy"));
const TransparentRecommendationLogic = lazy(() => import("./pages/legal/TransparentRecommendationLogic"));
const UserConsentForSmartFeatures = lazy(() => import("./pages/legal/UserConsentForSmartFeatures"));
const SubscriptionPlans = lazy(() => import("./pages/legal/SubscriptionPolicy.jsx"));
const CommissionModelOverview = lazy(() => import("./pages/legal/CommissionModelOverview"));
const AdPlacementFeaturedListings = lazy(() => import("./pages/legal/AdPlacementFeaturedListings"));
const AffiliateRevenueProgram = lazy(() => import("./pages/legal/AffiliateRevenueProgram"));

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <CartProvider>
              <ConversationProvider>
                <DarkModeProvider>
                  <LoadScript
                    googleMapsApiKey={process.env.REACT_APP_Maps_API_KEY}
                    libraries={["places"]}
                  >
                      <div className="flex flex-col min-h-screen">
                        <Toaster position="top-center" reverseOrder={false} />
                        <Navbar />
                      <ScrollToTop />
                      <Chatbot />

                      <div className="flex-grow">
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoader />}>
                        <Routes>
                          {/* Public Routes */}
                          <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route
                          path="/complete-profile"
                          element={<CompleteProfile />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="/reset-password/:token"
                          element={<ResetPassword />}
                        />
                        <Route path="/verify-otp" element={<VerifyOtp />} />
                        <Route path="/materials" element={<Materials />} />
                        <Route path="/product/:id" element={<ProductDetails />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/support" element={<HelpAndSupport />} />

                        {/* Admin Routes */}
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <AdminDashboard />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/users"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <UserManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/products"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <ProductModeration />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                         <Route
                          path="/admin/complaints"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <ComplaintManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                         <Route
                          path="/admin/contacts"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <ContactMessages />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/announcements"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <Announcements />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/system-health"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <SystemHealth />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/admin/settings"
                          element={
                            <ProtectedRoute allowedRole="admin">
                              <AdminLayout>
                                <AdminSettings />
                              </AdminLayout>
                            </ProtectedRoute>
                          }
                        />

                        {/* Customer routes  */}
                        <Route
                          path="/customer/settings"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <CustomerSettingsPage />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/notifications"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerNotifications />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/order-success/:orderId"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <OrderSuccess />
                            </ProtectedRoute>
                          }
                        />
                        {/* Order Details */}
                        <Route
                          path="/customer/orders/:orderId"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <OrderDetails />
                            </ProtectedRoute>
                          }
                        />
                        {/* Write Review */}
                        <Route
                          path="/customer/orders/:orderId/review"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <WriteReview />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wishlist */}
                        <Route
                          path="/customer/wishlist"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <Wishlist />
                            </ProtectedRoute>
                          }
                        />
                        {/* Address Management */}
                        <Route
                          path="/customer/addresses"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <AddressManagement />
                            </ProtectedRoute>
                          }
                        />
                        {/* Quick Reorder */}
                        <Route
                          path="/customer/reorder"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <QuickReorder />
                            </ProtectedRoute>
                          }
                        />
                        {/* Invoices */}
                        <Route
                          path="/customer/invoices"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <Invoices />
                            </ProtectedRoute>
                          }
                        />
                        {/* Payment Methods */}
                        <Route
                          path="/customer/payment-methods"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <PaymentMethods />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/offers"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <Offers />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/suppliers"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <NearbySuppliers />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/alerts"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <ProductAlerts />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/analytics"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <SpendingAnalytics />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/order-tracking"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <OrderTracking />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/materials"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <Materials />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/support"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <HelpAndSupport />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        {/* Customer Quote Routes */}
                        <Route
                          path="/customer/quotes/request"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <RequestQuote />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/quotes"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <MyQuotes />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/quote-requests"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <QuoteRequests />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/quotes/:id"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <QuoteDetails />
                            </ProtectedRoute>
                          }
                        />
                        {/* Supplier Quote Routes */}
                        <Route
                          path="/supplier/quotes"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <QuoteRequests />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/quotes/respond/:id"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <RespondToQuote />
                            </ProtectedRoute>
                          }
                        />
                        {/* legal routes  */}
                        <Route
                          path="/legal/supplier-partner"
                          element={<SupplierPartner />}
                        />
                        <Route
                          path="/legal/core-policies"
                          element={<CorePolicies />}
                        />
                        <Route
                          path="/legal/customer-platform"
                          element={<CustomerPlatform />}
                        />
                        <Route
                          path="/legal/smart-platform"
                          element={<SmartPlatform />}
                        />
                        <Route
                          path="/legal/monetization"
                          element={<Monetization />}
                        />
                        {/* legal of core polices  */}
                        <Route
                          path="/legal/termsandconditions"
                          element={<TermsAndConditions />}
                        />
                        <Route
                          path="/legal/privacypolicy"
                          element={<PrivacyPolicy />}
                        />
                        <Route
                          path="/legal/cookiepolicy"
                          element={<CookiePolicy />}
                        />
                        <Route
                          path="/legal/disclaimer"
                          element={<Disclaimer />}
                        />
                        <Route
                          path="/legal/dataprotection"
                          element={<DataProtection />}
                        />
                        <Route
                          path="/legal/securitypractices"
                          element={<SecurityPractices />}
                        />
                        <Route
                          path="/legal/supplieragreement"
                          element={<SupplierAgreement />}
                        />
                        <Route
                          path="/legal/productguidelines"
                          element={<ProductGuidelines />}
                        />
                        <Route
                          path="/legal/logisticsagreement"
                          element={<LogisticsAgreement />}
                        />
                        <Route
                          path="/legal/pricingpolicy"
                          element={<PricingPolicy />}
                        />
                        <Route
                          path="/legal/returnrefundpolicy"
                          element={<ReturnRefundPolicy />}
                        />
                        <Route
                          path="/legal/shippingpolicy"
                          element={<ShippingPolicy />}
                        />
                        <Route
                          path="/legal/cancellationpolicy"
                          element={<CancellationPolicy />}
                        />
                        {/* Customer Policy Pages */}
                        <Route
                          path="/legal/orderplacementtracking"
                          element={<OrderPlacementTracking />}
                        />
                        <Route
                          path="/legal/paymentrefundguidelines"
                          element={<PaymentRefundGuidelines />}
                        />
                        <Route
                          path="/legal/reviewsystemintegrity"
                          element={<ReviewSystemIntegrity />}
                        />
                        <Route
                          path="/legal/supportgrievance"
                          element={<SupportGrievanceRedressal />}
                        />
                        <Route
                          path="/legal/customeragreement"
                          element={<CustomerAgreement />}
                        />
                        <Route
                          path="/legal/platformpolicy"
                          element={<PlatformPolicy />}
                        />
                        <Route
                          path="/legal/reviewpolicy"
                          element={<GeneralReviewPolicy />}
                        />
                        <Route path="/legal/disputes" element={<Disputes />} />
                        <Route
                          path="/legal/locationterms"
                          element={<LocationTerms />}
                        />
                        <Route
                          path="/legal/laborterms"
                          element={<LaborTerms />}
                        />
                        {/* Smart Platform Policies */}
                        <Route
                          path="/legal/aigeneratedinsightsdisclaimer"
                          element={<AIGeneratedInsightsDisclaimer />}
                        />
                        <Route
                          path="/legal/algorithmicfairnesspolicy"
                          element={<AlgorithmicFairnessPolicy />}
                        />
                        <Route
                          path="/legal/transparentrecommendationlogic"
                          element={<TransparentRecommendationLogic />}
                        />
                        <Route
                          path="/legal/userconsentforsmartfeatures"
                          element={<UserConsentForSmartFeatures />}
                        />
                        {/* Monetization & Revenue Policies */}
                        <Route
                          path="/legal/suppliersubscriptionplans"
                          element={<SubscriptionPlans />}
                        />
                        <Route
                          path="/legal/commissionmodel"
                          element={<CommissionModelOverview />}
                        />
                        <Route
                          path="/legal/adplacement"
                          element={<AdPlacementFeaturedListings />}
                        />
                        <Route
                          path="/legal/affiliaterevenueprogram"
                          element={<AffiliateRevenueProgram />}
                        />
                        {/* Supplier Routes */}
                        <Route
                          path="/supplier/myproducts"
                          element={<MyProducts />}
                        />
                        <Route
                          path="/supplier/add-product"
                          element={<AddProduct />}
                        />
                        <Route
                          path="/supplier/bulk-upload"
                          element={<BulkUpload />}
                        />
                        <Route
                          path="/supplier/location"
                          element={<LocationPage />}
                        />
                        <Route
                          path="/supplier/settings"
                          element={<BusinessProfilePage />}
                        />
                        <Route
                          path="/supplier/profile"
                          element={<BusinessProfilePage />}
                        />
                        <Route
                          path="/supplier/activity-logs"
                          element={<ActivityLogsPage />}
                        />
                        <Route
                          path="/supplier/top-products"
                          element={<TopProductsPage />}
                        />
                        <Route
                          path="/supplier/customer-feedback"
                          element={<CustomerFeedbackPage />}
                        />
                        <Route
                          path="/supplier/delivery-status"
                          element={<DeliveryStatusPage />}
                        />
                        <Route
                          path="/supplier/notifications"
                          element={<NotificationsPage />}
                        />
                        {/* License Route Removed - Validated as sub-component of Profile */}
                        <Route
                          path="/supplier/edit-product/:id"
                          element={<EditProduct />}
                        />
                        <Route
                          path="/supplier/products/:id"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <ProductDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/categories"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <CategoriesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/orders"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <OrdersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/analytics"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <AnalyticsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/payments"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <PaymentsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/sync-inventory"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <SyncInventoryPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/create-offer"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <OfferFormPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/offers/:id/edit"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <OfferFormPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier/offers"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <ManageOffersPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/category/:category"
                          element={<CategoryPage />}
                        />
                        <Route
                          path="/customer/category/:category"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <CategoryPage />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/product/:id"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerLayout>
                                <ProductDetails />
                              </CustomerLayout>
                            </ProtectedRoute>
                          }
                        />
                        {/* Customer Routes (Protected) */}
                        <Route
                          path="/customer-dashboard"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <CustomerDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/orders"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <MyOrders />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/cart"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <Cart />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/customer/checkout"
                          element={
                            <ProtectedRoute allowedRole="customer">
                              <Checkout />
                            </ProtectedRoute>
                          }
                        />

                        {/* General Protected Routes */}
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/supplier-dashboard"
                          element={
                            <ProtectedRoute allowedRole="supplier">
                              <SupplierDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <Profile />
                            </ProtectedRoute>
                          }
                        />
                        {/* Fallback */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                        </Suspense>
                      </ErrorBoundary>
                    </div>

                      <Footer />
                    </div>
                  </LoadScript>
                </DarkModeProvider>
              </ConversationProvider>
            </CartProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
